import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import type {
  salesOrders,
  salesOrdersPatch,
} from "types/api-types/crm/sales-orders/sales-orders.types";
import type { accounts } from "types/api-types/crm/accounts/accounts.types";
import type { user } from "types/api-types/user/user/user.types";
import type { job, jobData, jobPatch } from "types/api-types/job/job/job.types";

interface ProjectData {
  salesOrderId: number | null;
  customerId: number | null;
  projectName: string;
  projectNumber: string;
  projectManager: number | null;
  fieldForeman: number | null;
  engineer: number | null;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  contractDocuments: File[];
  milestones: Array<{
    name: string;
    targetDate: Date | null;
  }>;
}

class ProjectInitializationWorkflowStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  projectData: ProjectData = {
    salesOrderId: null,
    customerId: null,
    projectName: "",
    projectNumber: "",
    projectManager: null,
    fieldForeman: null,
    engineer: null,
    shippingAddress: "",
    shippingCity: "",
    shippingState: "",
    shippingZip: "",
    contractDocuments: [],
    milestones: [
      { name: "Engineering Complete", targetDate: null },
      { name: "Production Start", targetDate: null },
      { name: "First Shipment", targetDate: null },
      { name: "Installation Complete", targetDate: null },
    ],
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateProjectData = <K extends keyof ProjectData>(
    field: K,
    value: ProjectData[K]
  ) => {
    this.projectData[field] = value;
  };

  selectSalesOrder = (salesOrderId: number) => {
    const salesOrdersStore = this.rootStore
      .salesOrdersStore as ServiceStore;
    const salesOrder = salesOrdersStore?.entities.find(
      (so) => so.id === salesOrderId
    );

    runInAction(() => {
      this.projectData.salesOrderId = salesOrderId;
      this.projectData.customerId = (salesOrder?.accountId as number) || null;
    });
  };

  addContractDocument = (files: FileList) => {
    const newFiles = Array.from(files);
    runInAction(() => {
      this.projectData.contractDocuments = [
        ...this.projectData.contractDocuments,
        ...newFiles,
      ];
    });
  };

  removeContractDocument = (index: number) => {
    this.projectData.contractDocuments =
      this.projectData.contractDocuments.filter((_, i) => i !== index);
  };

  updateMilestone = (index: number, date: Date | null) => {
    this.projectData.milestones[index].targetDate = date;
  };

  // Async actions
  createProject = async () => {
    this.loading = true;
    this.error = null;

    try {
      const salesOrdersStore = this.rootStore
        .salesOrdersStore as ServiceStore;
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const userStore = this.rootStore.userStore as ServiceStore;

      // Find the sales order
      const salesOrder = salesOrdersStore?.entities.find(
        (so) => so.id === this.projectData.salesOrderId
      );

      if (!salesOrder) {
        throw new Error("Sales order not found");
      }

      // Get user names for fieldForeman and engineer
      const fieldForemanUser = userStore?.entities.find(
        (u) => u.id === this.projectData.fieldForeman
      );
      const engineerUser = userStore?.entities.find(
        (u) => u.id === this.projectData.engineer
      );

      // Parse and validate jobNumber
      const jobNumber = parseInt(this.projectData.projectNumber, 10);
      if (isNaN(jobNumber)) {
        throw new Error("Project number must be a valid number");
      }

      // Create the job/project with only required fields
      const newJob = await jobStore?.create({
        name: this.projectData.projectName,
        jobNumber: jobNumber,
      } as jobData);

      // Patch the job with additional fields
      if (newJob) {
        const patchData: jobPatch = {
          accountId: this.projectData.customerId || undefined,
          projectManagerId: this.projectData.projectManager || undefined,
          fieldForeman: fieldForemanUser
            ? `${fieldForemanUser.firstName} ${fieldForemanUser.lastName}`
            : undefined,
          engineer: engineerUser
            ? `${engineerUser.firstName} ${engineerUser.lastName}`
            : undefined,
          address: this.projectData.shippingAddress || undefined,
          city: this.projectData.shippingCity || undefined,
          stateLocation: this.projectData.shippingState || undefined,
          zip: this.projectData.shippingZip || undefined,
          poNumber: (salesOrder.poNumber as string) || undefined,
          stage: "Planning",
          priority: "Medium",
          progress: 0,
        };
        await jobStore?.patch(Number(newJob.id), patchData);
      }

      // Update sales order with job reference
      if (newJob) {
        const patchData: salesOrdersPatch = {
          jobId: newJob.id as number,
          status: "InProduction",
        };
        await salesOrdersStore?.patch(this.projectData.salesOrderId, patchData);
      }

      runInAction(() => {
        this.success = "Project initialized successfully!";
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to create project";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.projectData = {
      salesOrderId: null,
      customerId: null,
      projectName: "",
      projectNumber: "",
      projectManager: null,
      fieldForeman: null,
      engineer: null,
      shippingAddress: "",
      shippingCity: "",
      shippingState: "",
      shippingZip: "",
      contractDocuments: [],
      milestones: [
        { name: "Engineering Complete", targetDate: null },
        { name: "Production Start", targetDate: null },
        { name: "First Shipment", targetDate: null },
        { name: "Installation Complete", targetDate: null },
      ],
    };
  };

  // Computed values
  get canProceedFromStep1() {
    return !!this.projectData.salesOrderId;
  }

  get canProceedFromStep2() {
    return true; // Customer details are optional
  }

  get canProceedFromStep3() {
    return true; // Shipping location can be updated later
  }

  get canProceedFromStep4() {
    return true; // Documents are optional
  }

  get canCreateProject() {
    // Ensure projectNumber is a valid integer
    const jobNumber = parseInt(this.projectData.projectNumber, 10);
    return (
      !!this.projectData.salesOrderId &&
      !!this.projectData.projectName &&
      !!this.projectData.projectNumber &&
      !isNaN(jobNumber) &&
      jobNumber > 0
    );
  }

  get approvedSalesOrders(): salesOrders[] {
    const salesOrdersStore = this.rootStore
      .salesOrdersStore as ServiceStore;
    return (salesOrdersStore?.entities || []) as unknown as salesOrders[];
  }

  get accounts(): accounts[] {
    const accountsStore = this.rootStore
      .accountsStore as ServiceStore;
    return (accountsStore?.entities || []) as unknown as accounts[];
  }

  get users(): user[] {
    const userStore = this.rootStore.userStore as ServiceStore;
    return (userStore?.entities || []) as unknown as user[];
  }

  get projectManagers(): user[] {
    // Filter users by department or team since 'role' field doesn't exist
    return this.users.filter(
      (u) =>
        u.department?.toLowerCase().includes("project") ||
        u.team?.toLowerCase().includes("project")
    );
  }

  get engineers(): user[] {
    // Filter users by department or team for engineers
    return this.users.filter(
      (u) =>
        u.department?.toLowerCase().includes("engineer") ||
        u.team?.toLowerCase().includes("engineer")
    );
  }

  get foremen(): user[] {
    // Filter users by department or team for foremen
    return this.users.filter(
      (u) =>
        u.department?.toLowerCase().includes("field") ||
        u.team?.toLowerCase().includes("foreman")
    );
  }

  get selectedSalesOrder(): salesOrders | null {
    if (!this.projectData.salesOrderId) return null;
    const salesOrdersStore = this.rootStore
      .salesOrdersStore as ServiceStore;
    const entities = (salesOrdersStore?.entities || []) as unknown as salesOrders[];
    return (
      entities.find(
        (so) => so.id === this.projectData.salesOrderId
      ) || null
    );
  }

  get selectedCustomer(): accounts | null {
    if (!this.projectData.customerId) return null;
    return (
      this.accounts.find((a) => a.accountId === this.projectData.customerId) ||
      null
    );
  }
}

export default ProjectInitializationWorkflowStore;
